import React, { useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarClock,
  PartyPopper,
  Mail,
  DollarSign,
  Search,
  Megaphone,
  Users,
  Menu,
  LogOut,
  ChevronDown,
  ExternalLink,
  Eye,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { BrandFilter } from "@/components/BrandFilter";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/reservations", label: "Reservations", icon: CalendarClock },
  { to: "/admin/inquiries", label: "Event Inquiries", icon: PartyPopper },
  { to: "/admin/signups", label: "Newsletter", icon: Mail },
  { to: "/admin/revenue", label: "Revenue", icon: DollarSign },
  { to: "/admin/seo", label: "SEO & Web", icon: Search },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone },
  { to: "/admin/visitors", label: "Visitors", icon: Eye, corporateOnly: true },
  { to: "/admin/users", label: "Users", icon: Users, corporateOnly: true },
];

function NavItems({ onNavigate }) {
  const { isCorporate } = useAuth();
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => !n.corporateOnly || isCorporate).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-[12px] px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pb-ring)] ${
              isActive
                ? "bg-white/[0.14] text-white shadow-[0_1px_0_rgba(255,255,255,0.08)_inset]"
                : "text-white/75 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <item.icon className="h-[18px] w-[18px]" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-[#18344A]">
      <div className="px-6 py-6">
        <img src="/assets/logo-white.png" alt="Pack Brothers Hospitality" className="w-full max-w-[188px]" />
      </div>
      <div className="mt-2 flex-1 overflow-y-auto pb-6">
        <NavItems onNavigate={onNavigate} />
      </div>
      <div className="px-6 py-4 text-[11px] text-white/40">Corporate Command Center</div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[var(--pb-bg)]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] lg:block">
        <SidebarContent />
      </aside>

      <div className="lg:pl-[264px]">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-[var(--pb-border)] bg-white/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                className="lg:hidden"
              >
                <Menu className="h-5 w-5 text-[#18344A]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[264px] border-none p-0">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="hidden items-center gap-2 md:flex">
            <BrandFilter />
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <DateRangeFilter />
            <div className="md:hidden">
              <BrandFilter />
            </div>
            <Link
              to="/"
              data-testid="back-to-site-button"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--pb-border)] bg-white px-3 py-2 text-sm font-medium text-[#18344A] transition-colors hover:bg-[rgba(24,52,74,0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pb-ring)]"
            >
              <ExternalLink className="h-4 w-4 text-[#526E7E]" />
              <span className="hidden sm:inline">Back to Site</span>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 rounded-full border border-[var(--pb-border)] bg-white py-1 pl-1 pr-2 text-sm transition-colors hover:bg-[rgba(24,52,74,0.04)]"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-[#18344A] text-[11px] text-white">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden max-w-[120px] truncate font-medium text-[#30383C] sm:block">
                    {user?.name}
                  </span>
                  <ChevronDown className="hidden h-4 w-4 text-[#78909C] sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium text-[#30383C]">{user?.name}</div>
                  <div className="text-xs font-normal text-[rgba(48,56,60,0.6)]">{user?.email}</div>
                  <div className="mt-1 inline-flex rounded-full bg-[rgba(82,110,126,0.12)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[#526E7E]">
                    {user?.role}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-[#9a3a3a]">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main key={location.pathname} className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
